import { css } from '@emotion/react'
import { memo, useCallback, useRef } from 'react'
import { pinRoom } from '../../../../../actions/room'
import { addFriend, bookmarkModel } from '../../../../../actions/user'
import { openAuthenticationDrawer } from '../../../../../state/modals/state.drawer'
import { APPSTATE } from '../../../../../state/state.app'
import { setPinHelperID } from '../../../../../state/state.rooms'
import { isTouchDevice } from '../../../../../utilities/browser'
import {
    AppStateType,
    useAppStateSelector,
} from '../../../../Hooks/useAppState'
import { useIsRoomPinned, usePinHelperUser } from '../../../../Hooks/useRoom'
import AddBookmark from '../../../../Icons/AddBookmark'
import AddFriend from '../../../../Icons/AddFriend'
import Bookmarked from '../../../../Icons/Bookmarked'
import Friended from '../../../../Icons/Friended'
import Pin from '../../../../Icons/Pin'
import { getThemeSpacing } from '../../../../styles/appTheme'
import { palette } from '../../../../styles/colors'
import layers from '../../../../styles/layers'
import shape from '../../../../styles/shape'
import { COMPONENT_WIDTHS, PAPER_HEIGHTS } from '../../../../theme'

const pinDragHelperStyles = css({
    // transition: 'all 0.4s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    justifyContent: 'center',
    background: palette.background,
    boxShadow: layers.two,
    zIndex: PAPER_HEIGHTS.dragSidebar,
    position: 'absolute',
    right: 0,
    top: 'calc(var(--header-height) + 20%)',
    width: COMPONENT_WIDTHS.dragSidebar,
    marginRight: -(COMPONENT_WIDTHS.dragSidebar + 10),
    opacity: 0,
    borderTopLeftRadius: shape.borderRadius,
    borderBottomLeftRadius: shape.borderRadius,
    transition: 'margin 0.4s ease-in-out, opacity 0.4s ease-in-out',
})
// const pinDragHelperEmbeddedStyles = css({
//     transition: 'all 0.4s ease-in-out',
//     display: 'flex',
//     flexFlow: 'row nowrap',
//     alignContent: 'center',
//     justifyContent: 'center',
//     background: palette.background,
//     boxShadow: layers.two,
//     // zIndex: PAPER_HEIGHTS.dragSidebar,
//     // position: 'absolute',
//     // right: 0,
//     // top: 'calc(var(--header-height) + 20%)',
//     width: 180,
//     height: COMPONENT_WIDTHS.dragSidebar,
//     // marginRight: -(COMPONENT_WIDTHS.dragSidebar + 10),
//     margin: getThemeSpacing(1),
//     opacity: 1,
//     borderRadius: shape.borderRadius,
//     // borderBottomLeftRadius: shape.borderRadius,
// })
const pinReceiverStyles = css({
    display: 'flex',
    height: 44,
    borderRadius: '50%', //shape.borderRadius,
    backgroundColor: palette.background,
    padding: getThemeSpacing(1),
    margin: `${getThemeSpacing(2)} ${getThemeSpacing(1)}`,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.4s ease-in-out',
})

export const showDragHelper = (id: number) => {
    if (APPSTATE.app.isMobile || isTouchDevice) return
    setPinHelperID(id)
    const pinDragHelper = document.querySelector(
        '#pin-drag-helper',
    ) as HTMLDivElement
    if (pinDragHelper) {
        pinDragHelper.style.marginRight = '0px'
        pinDragHelper.style.opacity = '1'
    }
}

const getDistance = (mouseX: number, mouseY: number, elem: HTMLElement) => {
    const { top, left, bottom, right } = elem.getBoundingClientRect()
    const centerY = top + (bottom - top) / 2
    const centerX = left + (right - left) / 2
    const distance = Math.hypot(centerX - mouseX, centerY - mouseY)

    return distance
}

export const highlightDragHelper = (mouseX: number, mouseY: number) => {
    const pinDragHelperPin = document.querySelector(
        '#pin-drag-helper-pin',
    ) as HTMLDivElement
    const pinDragHelperFriend = document.querySelector(
        '#pin-drag-helper-friend',
    ) as HTMLDivElement
    const pinDragHelperBookmark = document.querySelector(
        '#pin-drag-helper-bookmark',
    ) as HTMLDivElement
    // find the distances we are to the icons and scale/color the closest icon
    const pinDistance = getDistance(mouseX, mouseY, pinDragHelperPin)
    const friendDistance = getDistance(mouseX, mouseY, pinDragHelperFriend)
    const bookmarkDistance = getDistance(mouseX, mouseY, pinDragHelperBookmark)

    if (pinDistance < bookmarkDistance && pinDistance < friendDistance) {
        if (pinDistance < 350) {
            const scalar = 1 + Math.min(1 / (pinDistance / 35), 0.75)

            pinDragHelperPin.style.scale = scalar + ''
        }
    } else {
        pinDragHelperPin.style.scale = '1'
    }
    if (friendDistance < bookmarkDistance && friendDistance < pinDistance) {
        if (friendDistance < 500) {
            const scalar = 1 + Math.min(1 / (friendDistance / 35), 0.75)

            pinDragHelperFriend.style.scale = scalar + ''
        }
    } else {
        pinDragHelperFriend.style.scale = '1'
    }
    if (bookmarkDistance < pinDistance && bookmarkDistance < friendDistance) {
        if (bookmarkDistance < 350) {
            const scalar = 1 + Math.min(1 / (bookmarkDistance / 35), 0.75)

            pinDragHelperBookmark.style.scale = scalar + ''
        }
    } else {
        pinDragHelperBookmark.style.scale = '1'
    }
}

export const hideDragHelper = () => {
    // setPinHelperID()
    const pinDragHelper = document.querySelector(
        '#pin-drag-helper',
    ) as HTMLDivElement
    if (pinDragHelper) {
        pinDragHelper.style.marginRight = `-${
            COMPONENT_WIDTHS.dragSidebar + 10
        }px`
        pinDragHelper.style.opacity = '0'
    }
}

const PinReceiver = memo(
    ({
        id,
        children,
        onDrop,
    }: {
        id: string
        children: any
        onDrop: () => void
    }) => {
        const receiverRef = useRef(null)
        const dropPin = useCallback(() => {
            if (receiverRef.current) {
                receiverRef.current.style.backgroundColor = palette.background
            }
            onDrop()
            hideDragHelper()
        }, [onDrop])

        const dragOver = useCallback((event) => {
            if (receiverRef.current) {
                receiverRef.current.style.backgroundColor =
                    palette.layerSecondaryShadow
            }
            event.preventDefault()
            event.stopPropagation()
        }, [])
        const dragEnter = useCallback((event) => {
            if (receiverRef.current) {
                receiverRef.current.style.backgroundColor =
                    palette.layerSecondaryShadow
            }
            event.preventDefault()
        }, [])
        const dragLeave = useCallback((event) => {
            if (receiverRef.current) {
                receiverRef.current.style.backgroundColor = palette.background
            }
            event.preventDefault()
        }, [])
        return (
            <div
                id={id}
                ref={receiverRef}
                css={pinReceiverStyles}
                onDrop={dropPin}
                onDragOver={dragOver}
                onDragEnter={dragEnter}
                onDragLeave={dragLeave}
            >
                {/* <span>Pin Room</span> */}
                {children}
            </div>
        )
    },
)

export const PinDragHelper = memo(() => {
    const authenticated = useAppStateSelector(AppStateType.APP, 'authenticated')
    const { id, isFriend, isBookmark } = usePinHelperUser()
    const isRoomPinned = useIsRoomPinned(id)
    const onDropAddFriend = useCallback(() => {
        // if (isFriend) removeFriend(id)
        // else
        if (authenticated) addFriend(id)
        else openAuthenticationDrawer()
    }, [id, authenticated])
    const onDropAddBookmark = useCallback(() => {
        // if (isBookmark) removeBookmark(id)
        // else
        if (authenticated) bookmarkModel(id)
        else openAuthenticationDrawer()
    }, [id, authenticated])
    const onDropPin = useCallback(() => {
        pinRoom(id)
    }, [id])
    return (
        <div id="pin-drag-helper" css={pinDragHelperStyles}>
            <PinReceiver id="pin-drag-helper-pin" onDrop={onDropPin}>
                <Pin size={1.25} filled={isRoomPinned} />
            </PinReceiver>
            <PinReceiver id="pin-drag-helper-friend" onDrop={onDropAddFriend}>
                {isFriend ? (
                    <Friended size={1.25} />
                ) : (
                    <AddFriend size={1.25} />
                )}
            </PinReceiver>
            <PinReceiver
                id="pin-drag-helper-bookmark"
                onDrop={onDropAddBookmark}
            >
                {isBookmark ? (
                    <Bookmarked size={1.25} />
                ) : (
                    <AddBookmark size={1.25} />
                )}
            </PinReceiver>
        </div>
    )
})
